/**
 * useWalletDetail — W1.5
 *
 * Parallelabfragen: Balance + Tokens (useQueries).
 * Transactions werden separat via useWalletTransactions geholt (kein Blocking).
 */
import { useQueries } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Lokale Typen ---

export type WalletBalance = {
  hnt: number
  iot: number
  mobile: number
  dc: number
  sol_lamports: number
  cached_at: string
  stale: boolean
}

export type WalletToken = {
  asset_type: string
  symbol: string
  amount: number
  decimals: number
  mint_address: string
}

export type WalletBalanceResponse = {
  data: WalletBalance
}

export type WalletTokensResponse = {
  data: WalletToken[]
}

// --- Fetch-Helper ---

async function fetchBalance(walletId: number): Promise<WalletBalanceResponse> {
  const result = await apiClient.GET('/api/v2/wallets/{id}/balance', {
    params: { path: { id: walletId } },
  })
  if (result.error) throw result.error
  // S10-24 F-064: queryFn must NEVER return undefined — React Query throws
  // "Query data cannot be undefined". If data is absent with no error, throw
  // a descriptive error so the query lands in error state rather than crashing.
  if (!result.data) {
    throw new Error('Query data is undefined for wallet balance — expected error was not set by fetcher')
  }
  return result.data as unknown as WalletBalanceResponse
}

async function fetchTokens(walletId: number): Promise<WalletTokensResponse> {
  const result = await apiClient.GET('/api/v2/wallets/{id}/tokens', {
    params: { path: { id: walletId } },
  })
  if (result.error) throw result.error
  // S10-24 F-064: queryFn must NEVER return undefined — React Query throws
  // "Query data cannot be undefined". If data is absent with no error, throw
  // a descriptive error so the query lands in error state rather than crashing.
  if (!result.data) {
    throw new Error('Query data is undefined for wallet tokens — expected error was not set by fetcher')
  }
  return result.data as unknown as WalletTokensResponse
}

// --- Hook ---

type UseWalletDetailResult = {
  balance: WalletBalance | undefined
  tokens: WalletToken[] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useWalletDetail(walletId: number): UseWalletDetailResult {
  const results = useQueries({
    queries: [
      {
        queryKey: ['wallet', walletId, 'balance'],
        queryFn: () => fetchBalance(walletId),
      },
      {
        queryKey: ['wallet', walletId, 'tokens'],
        queryFn: () => fetchTokens(walletId),
      },
    ],
  })

  const [balanceQuery, tokensQuery] = results

  return {
    balance: balanceQuery.data?.data,
    tokens: tokensQuery.data?.data,
    isLoading: balanceQuery.isLoading || tokensQuery.isLoading,
    isError: balanceQuery.isError || tokensQuery.isError,
    refetch: () => {
      void balanceQuery.refetch()
      void tokensQuery.refetch()
    },
  }
}
